import type { BankCreditTxn } from "./bank-statement-types";

export type NumberedBankCredit = BankCreditTxn & {
  voucherNumber: string;
};

export class AssignVoucherNumberError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AssignVoucherNumberError";
  }
}

export type AssignVoucherNumbersOptions = {
  /** Prepended to each padded sequential number (may be empty). */
  prefix: string;
  /**
   * Starting voucher sequence as a digit string (leading zeros set pad width).
   * Example: `"0007"` → first voucher `…0007`, next `…0008`.
   */
  seed: string;
};

/** Digits only (leading zeros allowed). Rejects empty, signs, decimals, whitespace. */
export function parseVoucherSeed(seed: string): { start: number; width: number } {
  const raw = String(seed ?? "");
  if (!/^\d+$/.test(raw)) {
    throw new AssignVoucherNumberError(
      'Seed must be a non-negative integer string (e.g. "0007"). Leading zeros are preserved.'
    );
  }
  return { start: Number(raw), width: raw.length };
}

function compareCredits(a: BankCreditTxn, b: BankCreditTxn): number {
  const ta = a.txnDate.getTime();
  const tb = b.txnDate.getTime();
  if (ta !== tb) return ta - tb;
  return a.txnNo.localeCompare(b.txnNo, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Sort credits by txn date ascending (stable secondary: txn no), then assign
 * `prefix + zeroPaddedSequence` starting at `seed`.
 */
export function assignVoucherNumbers(
  credits: BankCreditTxn[],
  options: AssignVoucherNumbersOptions
): NumberedBankCredit[] {
  const { start, width } = parseVoucherSeed(options.seed);
  const prefix = String(options.prefix ?? "").trim();

  const sorted = [...credits].sort(compareCredits);

  return sorted.map((credit, index) => {
    const seq = String(start + index).padStart(width, "0");
    return {
      ...credit,
      voucherNumber: `${prefix}${seq}`,
    };
  });
}
